<template>
  <div
    v-if="showWelcomeVideoPopup"
    class="popup-holder welcome-video-holder"
    @click.self="close"
  >
    <div class="popup-wrap welcome-video-popup">
      <!--
        No .popup-head: the close control floats over the video instead, so the
        modal is pure video and its size is governed by one constraint rather
        than "viewport minus header".
      -->
      <button
        type="button"
        class="welcome-video_close icon-close"
        aria-label="Close welcome video"
        @click="close"
      ></button>

      <p v-if="loadFailed" class="welcome-video_error">
        This video could not be loaded. Please try again in a moment.
      </p>
      <video
        v-show="!loadFailed"
        ref="video"
        class="welcome-video"
        :src="videoUrl"
        :poster="posterUrl"
        controls
        playsinline
        preload="metadata"
        @play="onPlay"
        @timeupdate="onTimeUpdate"
        @ended="onEnded"
        @error="onError"
      >
        Your browser does not support embedded video.
      </video>
    </div>
  </div>
</template>

<script>
import { mapStores } from "pinia";
import { useAppStore } from "@/stores/app-store";
import {
  ONBOARDING_STEPS,
  useOnboardingChecklistStore,
} from "@/stores/onboarding-checklist-store";
import { analytics } from "@/services/analytics.service";

/**
 * Hosted alongside the other product demos on the marketing site. The override
 * exists so local development can point at an already-published clip while the
 * real welcome video is still being produced.
 */
export const WELCOME_VIDEO_URL =
  import.meta.env.VITE_WELCOME_VIDEO_URL ||
  "https://audioscribe.io/demos/welcome.mp4";

export const WELCOME_VIDEO_POSTER_URL =
  import.meta.env.VITE_WELCOME_VIDEO_POSTER_URL ||
  "https://audioscribe.io/demos/welcome-poster.webp";

/** Fraction of the video that counts as "watched". */
export const COMPLETION_THRESHOLD = 0.9;

export default {
  name: "WelcomeVideoPopup",
  props: ["showWelcomeVideoPopup"],
  computed: {
    ...mapStores(useAppStore, useOnboardingChecklistStore),
    videoUrl: () => WELCOME_VIDEO_URL,
    posterUrl: () => WELCOME_VIDEO_POSTER_URL,
  },
  data() {
    return {
      // Reset per-open: a second viewing is a genuine second play.
      hasTrackedPlay: false,
      hasCompleted: false,
      // Never reset: completion is a one-off milestone, so reopening the modal
      // and watching again must not emit a second completion event.
      hasFiredCompletionAnalytics: false,
      loadFailed: false,
    };
  },
  watch: {
    showWelcomeVideoPopup(isOpen) {
      if (isOpen) {
        this.hasTrackedPlay = false;
        this.hasCompleted = false;
        this.loadFailed = false;
      } else {
        this.pause();
      }
    },
  },
  methods: {
    close() {
      this.pause();
      this.appStore.showWelcomeVideoPopup = false;
    },
    pause() {
      const video = this.$refs.video;
      if (video && typeof video.pause === "function" && !video.paused) {
        video.pause();
      }
    },
    onPlay() {
      if (this.hasTrackedPlay) return;
      this.hasTrackedPlay = true;
      analytics.welcomeVideoPlayed();
    },
    onTimeUpdate(event) {
      const { currentTime, duration } = event?.target ?? {};
      if (!duration || !Number.isFinite(duration)) return;
      if (currentTime / duration >= COMPLETION_THRESHOLD) {
        this.markComplete();
      }
    },
    onEnded() {
      this.markComplete();
    },
    onError() {
      this.loadFailed = true;
    },
    markComplete() {
      // Per-open guard so timeupdate does not fire this on every tick.
      if (this.hasCompleted) return;
      this.hasCompleted = true;

      if (!this.hasFiredCompletionAnalytics) {
        this.hasFiredCompletionAnalytics = true;
        analytics.welcomeVideoCompleted();
      }

      // The store dedupes and, on a failed report, clears its own guard so a
      // later viewing retries. Nothing here needs to track that.
      this.onboardingChecklistStore.markStep(ONBOARDING_STEPS.WATCH_TOUR);
    },
  },
};
</script>

<style>
/*
  Sizing note: the shared rule is `.popup-holder .popup-wrap`, which caps modals
  at 500px from 1024px up and 737px from 1366px up. That is right for forms and
  far too small for a screen recording, so these selectors are deliberately more
  specific in order to win.

  The width is whichever of three limits binds first:
    - 1920px, the source's native width, so it is never upscaled into softness
      but a large monitor is still used properly;
    - 94vw, to keep a margin on narrow windows;
    - the width a 16:9 frame can reach in the height available, which is what
      actually binds on laptops and any short or split-screen window.
  Because the width already accounts for the height, the video always fits fully
  on screen — no page scroll, and no need to go fullscreen to read it.
*/
.popup-holder.welcome-video-holder {
  align-items: center;
  padding: 16px;
}

.popup-holder.welcome-video-holder .popup-wrap.welcome-video-popup {
  position: relative;
  width: min(1920px, 94vw, calc(94vh * 16 / 9));
  /* Repeated with dvh so browsers that support it shrink the video when mobile
     address-bar chrome expands, instead of letting it run under the chrome.
     Browsers without dvh simply keep the vh line above. */
  width: min(1920px, 94vw, calc(94dvh * 16 / 9));
  max-width: none;
  max-height: none;
  overflow: visible;
  background: #000;
  line-height: 0; /* no descender gap under the video */
}

.welcome-video {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  background: #000;
  object-fit: contain;
}

.welcome-video_close {
  position: absolute;
  top: -14px;
  right: -14px;
  z-index: 2;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #fff;
  color: #0d2a3a;
  font-size: 13px;
  line-height: 34px;
  text-align: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
}

.welcome-video_close:hover {
  background: #f2f2f2;
}

/*
  The button hangs 14px outside the modal corner, which needs the gap between
  modal and viewport edge to be at least that. When height is the binding
  constraint the modal is 94vh tall, so that gap is only 3vh — under roughly
  530px of viewport height it is smaller than the button and the corner clips
  off-screen. Landscape phones (e.g. 844x390) hit this while still being far
  too wide for a width-based query, so tuck the button inside on either axis.
*/
@media (max-width: 600px), (max-height: 600px) {
  .welcome-video_close {
    top: 8px;
    right: 8px;
  }
}

.welcome-video_error {
  margin: 0;
  padding: 48px 24px;
  color: #fff;
  text-align: center;
  line-height: 1.5;
}
</style>
